/**
 * AddressSearchInput — a search control of its own, asking Lantmäteriet through
 * our backend (GF-77).
 *
 * ═══ IT OWNS NO FORM FIELD, AND THAT IS THE POINT ═══
 *
 * The query lives in this component's own state and reaches nothing else. The
 * only thing that ever leaves is a POSITION, through onSelect. The address text
 * belongs to AddressTextInput and is never touched from here — not by typing a
 * query, not by picking a result.
 *
 * The first version of this component did own the address field, and it was
 * wrong for a reason worth keeping: one box cannot be both "what you are
 * searching for" and "what will be stored". A register row written "Fabriken"
 * can only be found by typing something else, so searching destroyed the name
 * it was meant to rescue — GF-65's error class arriving through the very
 * feature built to solve it, on exactly the population it was built for (the
 * 34 rows sent to review on 2026-09-16).
 *
 * ═══ NOTHING IS FILTERED, NOTHING IS RE-RANKED ═══
 *
 * Every answer Lantmäteriet gives is shown, in Lantmäteriet's own order. The
 * batch pipeline withholds a place-less answer because nobody is looking at it
 * (GF-54); here somebody is, so the same fact is a caption instead. Sorting is
 * not ours either — GF-56 measured a case where the correct answer was second.
 *
 * Showing everything is only safe because LM's answer carries its own kommun
 * and postort ("Eda Köla Korterud Gläntan 1 67292 Årjäng"): measured on 709
 * rows, 640 of 640 answers name the kommun and 638 carry a postcode. If that
 * ever stops being true, this decision has to be revisited.
 *
 * ═══ THE ICON IS A MAP PIN, NOT A CHECK MARK ═══
 *
 * AddressInput1's CheckCircleFilled means "the text came from the provider and
 * is therefore verified" — precisely what does NOT happen here. One symbol with
 * two meanings in the same app cannot be explained away, so this gets its own.
 *
 * NOTE ON LANGUAGE: code and comments English, UI text Swedish — project rule.
 */
import React, { useState, useEffect, useRef } from "react";
import { Input, Menu, Dropdown, Spin } from "antd";
import { EnvironmentFilled } from "@ant-design/icons";
import searchAddress, {
  SEARCH_MIN_LENGTH,
  SEARCH_MAX_LENGTH,
} from "../../api/searchAddress";
import { toPickedPosition } from "../../utils/addressPick";
import "./AddressInput.scss";

/**
 * Debounce, and the reason it is not cosmetic.
 *
 * GF-27: the old box called Mapbox on EVERY keystroke — the vendor's own
 * documented cost trap — so a 30-character address was 30 requests. Changing
 * the source without changing that would move the trap onto our own backend and
 * Lantmäteriet's quota, which the batch geocoder shares. The server enforces a
 * rate limit regardless; this keeps an ordinary typist from ever meeting it.
 */
const DEBOUNCE_MS = 400;

/**
 * What the backend's `notice` means to a human. UI text: Swedish.
 *
 * ⚠️ THERE IS NO "place_name_missing" HERE ANY MORE, and it must not come back
 * without a new measurement. It warned when the query carried no town, reusing
 * the batch pipeline's predicate — which anchors on a POSTCODE. Register rows
 * nearly always have one; an operator typing "Fabriken Vårgårda" never does.
 * The warning would therefore have fired on most of the searches this box was
 * built for, and said something untrue while doing it. A warning that is almost
 * always on stops being read. Removed 2026-09-17; the batch gate is untouched.
 */
const NOTICE_TEXT = {
  empty_query: "Det fanns ingenting sökbart i texten. Skriv gatuadress och ort.",
};

const AddressSearchInput = ({
  defaultQuery = "",
  onSelect,
  // SWALLOWED ON PURPOSE, not unused by accident. `{...props}` is spread LAST
  // onto <Input>, so a `value`/`onChange` arriving from outside would silently
  // win over this component's own — and the moment the surrounding Form.Item
  // gains a `name`, antd injects exactly those two. The search box would become
  // a form field and "the query reaches nothing else" would stop being true
  // without a line in here changing. Spreading `{...props}` first was the other
  // option and is worse: it would drop placeholder and suffix.
  value: _neverBound,
  onChange: _neverBoundChange,
  ...props
}) => {
  const [query, setQuery] = useState(defaultQuery ?? "");
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | searching | done | error
  const [notice, setNotice] = useState(null);
  const [errorText, setErrorText] = useState(null);
  const [pickedAddress, setPickedAddress] = useState(null);

  const timer = useRef(null);
  /**
   * Which request an answer must belong to.
   *
   * Without it a slow early request can land AFTER a fast later one and
   * repopulate the list with results for text that is no longer in the box.
   * The operator would then be picking a position for a query they have already
   * replaced — and nothing on screen would say so. Incremented on every search
   * AND whenever the query drops below the minimum, so an answer in flight is
   * abandoned rather than merely ignored later.
   */
  const requestSeq = useRef(0);

  // Drop a pending debounce on unmount, so a closed modal cannot still fire.
  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const runSearch = async (text) => {
    const seq = ++requestSeq.current;
    setStatus("searching");
    setErrorText(null);
    const { response, error } = await searchAddress(text);
    if (seq !== requestSeq.current) return; // a newer search has taken over

    if (error) {
      setResults([]);
      setNotice(null);
      setStatus("error");
      // 429 is "wait", not "broken", and the two lead to different actions for
      // the operator. NOTE: no automatic retry — a client that retries on its
      // own turns one impatient operator into a loop against the rate limit it
      // just met.
      // THREE OUTCOMES, THREE MESSAGES. Before the 400 branch existed, a query
      // the server rejected as unsearchable rendered as "svarade inte" — wrong
      // cause, and the natural response (try again) burns rate-limit quota.
      // The Swedish sentences are written HERE rather than taken from
      // error.message: the server's text is fixed and harmless today, but
      // forwarding it would make every future BadRequest operator-visible.
      const waitSeconds = Number(error.retryAfterSeconds);
      setErrorText(
        error.status === 429
          ? `För många sökningar på kort tid. Vänta ${
              Number.isFinite(waitSeconds) && waitSeconds > 0 ? `${waitSeconds} sekunder` : "någon sekund"
            } och sök igen.`
          : error.status === 400
          ? `Söktexten går inte att söka på. Den måste vara mellan ${SEARCH_MIN_LENGTH} och ${SEARCH_MAX_LENGTH} tecken.`
          : "Adressökningen svarade inte. Försök igen om en stund."
      );
      return;
    }
    setResults(response?.results ?? []);
    setNotice(response?.notice ?? null);
    setStatus("done");
  };

  const handleChange = (event) => {
    const text = event.target.value;
    setQuery(text);
    // A new query invalidates the previous confirmation line, but NOT the
    // position already written to the form: the operator picked that
    // deliberately, and only picking again should replace it.
    setPickedAddress(null);

    if (timer.current) clearTimeout(timer.current);

    // BELOW THE MINIMUM NOTHING IS SENT, and anything in flight is abandoned.
    // LM's contract is 3-300 characters, so a shorter query is not merely
    // wasteful — it is a request the vendor would reject.
    const trimmed = text.trim();
    if (trimmed.length < SEARCH_MIN_LENGTH) {
      requestSeq.current++;
      setResults([]);
      setNotice(null);
      setErrorText(null);
      setStatus("idle");
      return;
    }
    // ABOVE THE CEILING NOTHING IS SENT EITHER. The server enforces the same
    // bound — it is Lantmäteriet's contract, not ours — but a round trip that
    // can only end in 400 is worth skipping, and "en operatör som klistrar in
    // en kolumn" is the exact case the rate limit's own comment names.
    if (trimmed.length > SEARCH_MAX_LENGTH) {
      requestSeq.current++;
      setResults([]);
      setNotice(null);
      setStatus("idle");
      setErrorText(`Söktexten är för lång (max ${SEARCH_MAX_LENGTH} tecken).`);
      return;
    }
    timer.current = setTimeout(() => runSearch(trimmed), DEBOUNCE_MS);
  };

  /**
   * THE PICK SENDS A POSITION AND NOTHING ELSE.
   *
   * toPickedPosition() is what enforces that, and it lives in utils so it can
   * be guarded without rendering anything. Do not bypass it by passing `result`
   * onward "just this once" — the protection has two halves (this one must not
   * SEND the provider's text, mergePickedPosition must not READ it) and either
   * looks sufficient until somebody changes the other.
   */
  const handlePick = (result) => {
    setPickedAddress(result.address || "");
    setResults([]);
    setNotice(null);
    onSelect && onSelect(toPickedPosition(result));
  };

  const menu = results.length ? (
    <Menu>
      {results.map((result, index) => (
        <Menu.Item key={`${result.lat},${result.lng},${index}`}>
          <div
            className="suggestion-item"
            style={{ backgroundColor: "#ffffff", cursor: "pointer" }}
            onClick={() => handlePick(result)}
          >
            {/* Lantmäteriet's own answer string, shown untouched. Reformatting
                it would be the first step towards hiding the very thing it is
                here for: that a hit may be in the wrong part of the country. */}
            <span>{result.address}</span>
          </div>
        </Menu.Item>
      ))}
    </Menu>
  ) : (
    <></>
  );

  return (
    <div className="AddressSearchInput">
      <Dropdown overlay={menu} placement="bottomLeft" visible={results.length > 0}>
        <Input
          onChange={handleChange}
          value={query}
          autoComplete="no"
          placeholder="Sök adress hos Lantmäteriet"
          suffix={
            status === "searching" ? (
              <Spin size="small" />
            ) : (
              <EnvironmentFilled
                style={{ color: pickedAddress !== null ? "#43C1C2" : "transparent" }}
              />
            )
          }
          {...props}
        />
      </Dropdown>

      {/* Every state says what HAPPENED, and "no answer" is never silent.
          GF-25's failure was that a broken call and "no matches" both rendered
          as an empty dropdown, so the operator could not tell them apart or
          know what to try next. */}
      {pickedAddress !== null && (
        <div className="AddressSearchInput-note AddressSearchInput-note--ok">
          Position hämtad från Lantmäteriet · adresstexten oförändrad
          {pickedAddress ? <div className="AddressSearchInput-echo">{pickedAddress}</div> : null}
        </div>
      )}
      {errorText && (
        <div className="AddressSearchInput-note AddressSearchInput-note--error">
          {errorText}
        </div>
      )}
      {notice && NOTICE_TEXT[notice] && (
        <div className="AddressSearchInput-note AddressSearchInput-note--warn">
          {NOTICE_TEXT[notice]}
        </div>
      )}
      {status === "done" && results.length === 0 && !notice && pickedAddress === null && (
        <div className="AddressSearchInput-note">
          Lantmäteriet hittade ingen adress som matchar. Kontrollera stavning och
          ort, eller skriv in koordinaterna för hand i fälten nedan.
        </div>
      )}
    </div>
  );
};

export default AddressSearchInput;
